/// JobDetail: Shows detailed information about a selected job and
/// provides Complete/Fail action buttons for active jobs.

import { useState } from "react";
import type { Job } from "@/types/engine";
import { useJobPanel } from "@/hooks/useJobPanel";

// ---------------------------------------------------------------------------
// State color mapping
// ---------------------------------------------------------------------------

const STATE_COLORS: Record<string, string> = {
  created: "text-zinc-400",
  activatable: "text-blue-400",
  activated: "text-yellow-400",
  completed: "text-green-400",
  failed: "text-red-400",
  timed_out: "text-orange-400",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface JobDetailProps {
  job: Job;
}

export function JobDetail({ job }: JobDetailProps) {
  const { completeJob, failJob } = useJobPanel();
  const [variables, setVariables] = useState("{}");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stateColor = STATE_COLORS[job.state] ?? "text-zinc-300";

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      const parsed = JSON.parse(variables);
      await completeJob(job.jobKey, parsed);
    } catch (err) {
      console.warn("Failed to complete job:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFail = async () => {
    setIsSubmitting(true);
    try {
      await failJob(job.jobKey, errorMessage || "Manual fail");
    } catch (err) {
      console.warn("Failed to fail job:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border-t border-zinc-700 p-3">
      {/* Job metadata grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
        <div>
          <span className="text-zinc-500">Key: </span>
          <span className="text-zinc-300 font-mono">{job.jobKey}</span>
        </div>
        <div>
          <span className="text-zinc-500">Type: </span>
          <span className="text-zinc-300">{job.jobType}</span>
        </div>
        <div>
          <span className="text-zinc-500">State: </span>
          <span className={stateColor}>{job.state}</span>
        </div>
        <div>
          <span className="text-zinc-500">Element: </span>
          <span className="text-zinc-300">{job.elementId}</span>
        </div>
        {job.workerId && (
          <div className="col-span-2">
            <span className="text-zinc-500">Worker: </span>
            <span className="text-zinc-300">{job.workerId}</span>
          </div>
        )}
      </div>

      {/* Job variables */}
      <div className="mb-2">
        <label className="text-[10px] text-zinc-500 mb-1 block">Job Variables</label>
        <pre className="bg-zinc-800 rounded p-2 text-[10px] text-zinc-300 max-h-20 overflow-y-auto font-mono">
          {JSON.stringify(job.variables, null, 2)}
        </pre>
      </div>

      {/* Actions (only for activated jobs) */}
      {job.state === "activated" && (
        <div className="space-y-2">
          {/* Complete variables input */}
          <div>
            <label className="text-[10px] text-zinc-500 mb-1 block">
              Complete Variables (JSON)
            </label>
            <input
              type="text"
              value={variables}
              onChange={(e) => setVariables(e.target.value)}
              className="w-full bg-zinc-800 text-xs text-zinc-200 rounded px-2 py-1 font-mono focus:outline-none border border-zinc-600"
              placeholder="{}"
            />
          </div>

          {/* Error message input */}
          <div>
            <label className="text-[10px] text-zinc-500 mb-1 block">Error Message (for fail)</label>
            <input
              type="text"
              value={errorMessage}
              onChange={(e) => setErrorMessage(e.target.value)}
              className="w-full bg-zinc-800 text-xs text-zinc-200 rounded px-2 py-1 focus:outline-none border border-zinc-600"
              placeholder="Manual fail"
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleComplete}
              disabled={isSubmitting}
              className="flex-1 bg-green-600 text-white text-xs py-1.5 rounded hover:bg-green-500 disabled:opacity-50 transition-colors"
            >
              Complete
            </button>
            <button
              type="button"
              onClick={handleFail}
              disabled={isSubmitting}
              className="flex-1 bg-red-600 text-white text-xs py-1.5 rounded hover:bg-red-500 disabled:opacity-50 transition-colors"
            >
              Fail
            </button>
          </div>
        </div>
      )}

      {/* Completed timestamp */}
      {job.completedAt && (
        <div className="text-[10px] text-zinc-500 mt-2">
          Completed: {new Date(job.completedAt).toLocaleTimeString()}
        </div>
      )}

      {/* Error message */}
      {job.errorMessage && (
        <div className="text-[10px] text-red-400 mt-1">Error: {job.errorMessage}</div>
      )}
    </div>
  );
}
